import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { post, get, patch } from '../../../../helpers/httpClient';
import { toast } from 'react-toastify';
import ImageUploader from '../../../../helpers/ImageUploader';
import PageBasicFields from '../../../../helpers/PageBasicFields';

function normalizeColumns(columns) {
  const cols = Array.isArray(columns) ? columns : [];
  return cols.map((col) => ({
    properties: Array.isArray(col?.properties)
      ? col.properties.map((p) => {
          const type = ['image', 'text', 'line'].includes((p?.type || '').toLowerCase()) ? (p.type || '').toLowerCase() : 'text';
          let meta = null;
          if (type === 'line') {
            const m = (p && p.meta && typeof p.meta === 'object') ? p.meta : {};
            const fs = String(m.fontSize ?? '').toLowerCase();
            const allowedFontSizes = ['heading', 'sub-heading', 'text'];
            meta = {
              prefix: String(m.prefix ?? ''),
              suffix: String(m.suffix ?? ''),
              fontSize: allowedFontSizes.includes(fs) ? fs : 'text',
            };
          }
          return {
            name: String(p?.name ?? ''),
            type,
            required: !!p?.required,
            show_name: p?.show_name === undefined ? true : !!p.show_name,
            meta,
          };
        })
      : [],
  }));
}

function GalleryItemEditor() {
  const navigate = useNavigate();
  const { id, pageId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!pageId;

  const [form, setForm] = useState({ slug: '', title: '', preview: null, template: null, extra: { props: {} } });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [parentPage, setParentPage] = useState(null);
  const [structure, setStructure] = useState({ columns: [] });

  const titleText = useMemo(() => (isEdit ? 'Edit Gallery item' : 'New Gallery item'), [isEdit]);

  async function loadTemplateColumns(websiteId, galleryTemplateId) {
    if (!galleryTemplateId) return [];
    const resp = await get(`websites/${websiteId}/gallery-templates/${galleryTemplateId}`);
    return resp?.gallery_template?.columns ?? [];
  }

  // Load current item (when editing) and determine parent, then load gallery template to get structure
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setError('');
        if (isEdit) {
          const itemResp = await get(`pages/${pageId}`);
          const item = itemResp?.page || itemResp;
          if (!item) throw new Error('Item not found');
          if (!mounted) return;
          setForm({
            slug: item.slug || '',
            title: item.title || '',
            description: item.description || '',
            preview: item.preview || null,
            template: item.template || null,
            components: item.components || { items: [] },
            extra: item.extra || { props: {} }
          });
          const parentId = item.parent?.id ?? item.parent;
          if (parentId) {
            try {
              const parentResp = await get(`pages/${parentId}`);
              const parent = parentResp?.page || parentResp;
              if (mounted) {
                setParentPage(parent);
                const columns = await loadTemplateColumns(id, parent?.extra?.galleryTemplateId);
                if (mounted) setStructure({ columns: normalizeColumns(columns) });
              }
            } catch {
              if (mounted) toast.warn('Could not load parent gallery template structure.');
            }
          }
        } else {
          // creating new: load parent by id from query
          const parentId = searchParams.get('parent');
          if (parentId) {
            const resp = await get(`pages/${parentId}`);
            const parent = resp?.page || resp;
            if (!parent) throw new Error('Parent gallery not found');
            if (mounted) {
              setParentPage(parent);
              const columns = await loadTemplateColumns(id, parent?.extra?.galleryTemplateId);
              if (mounted) setStructure({ columns: normalizeColumns(columns) });
            }
          } else {
            toast.warn('No parent selected. Please navigate from a gallery or provide a parent parameter.');
          }
        }
      } catch (e) {
        if (mounted) setError('Failed to load data.');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [id, pageId, isEdit, searchParams]);

  const handleChangeProp = (name, value) => {
    setForm(prev => ({ ...prev, extra: { ...(prev.extra || {}), props: { ...(prev.extra?.props || {}), [name]: value } } }));
  };

  const validateRequired = () => {
    const props = form?.extra?.props || {};
    const missing = [];
    for (const col of structure.columns || []) {
      for (const p of col.properties || []) {
        if (p.required) {
          const v = props[p.name];
          if (p.type === 'image') {
            if (!v || typeof v !== 'string' || v.trim() === '') missing.push(p.name);
          } else {
            if (v === undefined || v === null || String(v).trim() === '') missing.push(p.name);
          }
        }
      }
    }
    return missing;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.title.trim()) {
      toast.error('Title is required.');
      return;
    }
    const missing = validateRequired();
    if (missing.length > 0) {
      toast.error(`Please fill required fields: ${missing.join(', ')}`);
      return;
    }

    const payload = {
      type: 'gallery-item',
      slug: (form.slug || '').trim(),
      title: (form.title || '').trim(),
      description: (form.description || '').trim() || null,
      preview: form.preview || null,
      template: form.template || null,
      components: form.components || [],
      extra: form.extra || { props: {} },
    };

    try {
      setSaving(true);
      setError('');
      if (isEdit) {
        await patch(`websites/${id}/pages/${pageId}`, payload);
      } else {
        const parent = searchParams.get('parent');
        await post(`websites/${id}/pages`, { ...payload, order: 0, parent: parent || null });
      }
      navigate(`/websites/${id}?tab=structure`);
    } catch (err) {
      toast.error(isEdit ? 'Failed to update page.' : 'Failed to create page.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => navigate(-1);

  const propsMap = form?.extra?.props || {};

  const renderPropertyInput = (p) => {
    const label = p.name;
    const key = p.name;
    if (!key) return null;
    switch (p.type) {
      case 'image':
        return (
          <div key={key} className="mb-2">
            {label && <Form.Label>{label}</Form.Label>}
            <ImageUploader websiteId={id} onUploaded={(resp) => { handleChangeProp(key, resp.url); }} />
            {propsMap[key] && (
              <img src={propsMap[key]} alt={key} className="img-preview mt-2" />
            )}
          </div>
        );
      case 'line':
      case 'text':
      default:
        return (
          <div key={key} className="mb-2">
            {label && <Form.Label>{label}</Form.Label>}
            <Form.Control
              type="text"
              value={propsMap[key] || ''}
              onChange={(e) => handleChangeProp(key, e.target.value)}
              placeholder={p.type === 'line' ? `${p.meta?.prefix || ''}${p.show_name ? ` ${p.name}` : ''}${p.meta?.suffix || ''}`.trim() : 'Enter value'}
            />
          </div>
        );
    }
  };

  return (
    <Container className="mt-4">
      <Row>
        <Col>
              <div className="structure-node-header mb-3">
                <h2 className="m-0">{titleText}</h2>
                <span className="page-type-badge page-type-badge--item">Gallery Item</span>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}

              {loading ? (
                <div className="text-center my-3"><Spinner animation="border" /></div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  {!parentPage && (
                    <Alert variant="warning">Parent gallery is not set. Please go back and add an item under a gallery.</Alert>
                  )}

                  <PageBasicFields
                    form={form}
                    setForm={setForm}
                    websiteId={id}
                    slugPlaceholder="e.g. product-123"
                    titlePlaceholder="Enter title"
                    preserveEmptySlug={isEdit}
                  />

                  <div className="mt-3">
                    <div className="props-panel">
                      <div className="structure-node-header mb-2">
                        <strong>Item properties</strong>
                        {parentPage && <span className="text-secondary">From gallery: {parentPage.title || parentPage.slug}</span>}
                      </div>
                      {structure.columns.length === 0 && (
                        <p className="muted-hint mb-0">Parent gallery has no item structure defined.</p>
                      )}
                      {structure.columns.map((col, idx) => (
                        <div key={idx} className="props-column">
                          <p className="fw-semibold mb-2">Column #{idx + 1}</p>
                          {col.properties.length === 0 && (
                            <p className="text-secondary mb-0">No properties</p>
                          )}
                          {col.properties.map((p) => renderPropertyInput(p))}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-actions">
                    <Button type="submit" className="primary-button" disabled={saving}>
                      {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
                    </Button>
                    <Button className="primary-button" onClick={handleCancel} disabled={saving}>Cancel</Button>
                  </div>
                </Form>
              )}
        </Col>
      </Row>
    </Container>
  );
}

export default GalleryItemEditor;
